import { Injectable, inject } from '@angular/core';
import { Router, NavigationEnd, ActivatedRouteSnapshot } from '@angular/router';
import { BehaviorSubject, Observable } from 'rxjs';
import { filter } from 'rxjs/operators';
import { AuthService, UserRole } from './auth.service';

export type DomainType = 'SUPPLIERS' | 'PROFESSIONALS';

export interface NavigationContext {
  domainType: DomainType;
}

/**
 * Servizio per gestire il contesto di navigazione dell'applicazione.
 * Rileva automaticamente se l'utente è nella sezione SUPPLIERS o PROFESSIONALS
 * in base al ruolo dell'utente e all'URL corrente.
 */
@Injectable({
  providedIn: 'root'
})
export class NavigationContextService {
  
  private context$ = new BehaviorSubject<NavigationContext>({
    domainType: 'PROFESSIONALS'
  });

  private authService = inject(AuthService);

  constructor(private router: Router) {
    // Auto-detect dal routing
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe((event: NavigationEnd) => {
      // Prima prova a leggere il dominio dalle route data (nuove route esplicite)
      const domain = this.findDomainInRouteTree(this.router.routerState.snapshot.root);
      if (domain === 'SUPPLIERS') {
        this.setSupplierContext();
      } else if (domain === 'PROFESSIONALS') {
        this.setProfessionalContext();
      } else {
        this.detectContext(event.url);
      }
    });

    // Inizializza in base al contesto corrente
    this.detectContext(this.router.url);
  }

  /**
   * Rileva automaticamente il contesto in base al ruolo utente e URL
   * PRIORITÀ: Ruolo utente > Pattern URL
   */
  private detectContext(url?: string): void {
    const userRoles = this.authService.getUserRoles();
    
    // Strategia 1: Usa il ruolo dell'utente come criterio principale
    if (this.hasSupplierRole(userRoles)) {
      this.setSupplierContext();
      return;
    }
    
    if (this.hasProfessionalRole(userRoles)) {
      this.setProfessionalContext();
      return;
    }
    
    // Strategia 2: Per admin, usa l'URL
    if (url) {
      this.detectContextFromUrl(url);
    }
  }

  /**
   * Verifica se l'utente ha un ruolo di tipo SUPPLIER
   */
  private hasSupplierRole(roles: UserRole[]): boolean {
    return roles.some(role => 
      role === 'backoffice_supplier' || 
      role === 'professional' || 
      role === 'collaborator'
    );
  }

  /**
   * Verifica se l'utente ha un ruolo di tipo PROFESSIONAL
   */
  private hasProfessionalRole(roles: UserRole[]): boolean {
    return roles.includes('backoffice_professional');
  }

  /**
   * Rileva automaticamente il contesto in base all'URL (fallback per admin)
   */
  private detectContextFromUrl(url: string): void {
    const lowerUrl = url.toLowerCase();

    // Pattern espliciti nuove route con dominio nel path (priorità massima)
    if (lowerUrl.includes('/axa/supplier-')) {
      this.setSupplierContext();
      return;
    }
    if (lowerUrl.includes('/axa/professional-')) {
      this.setProfessionalContext();
      return;
    }

    // Pattern per SUPPLIERS
    const supplierPatterns = [
      '/supplier',
      '/responsible',
      '/sub-responsible',
      '/atto',
      '/nomina'
    ];
    
    // Pattern per PROFESSIONALS (più specifici per evitare falsi positivi)
    const professionalPatterns = [
      '/professional-',
      '/expert'
    ];
    
    const isSupplier = supplierPatterns.some(pattern => lowerUrl.includes(pattern));
    const isProfessional = professionalPatterns.some(pattern => lowerUrl.includes(pattern));
    
    if (isSupplier) {
      this.setSupplierContext();
    } else if (isProfessional) {
      this.setProfessionalContext();
    }
    // Mantiene il contesto corrente se non match
  }

  /**
   * Naviga l'albero delle route attive per trovare il dominio esplicito nel data
   */
  private findDomainInRouteTree(route: ActivatedRouteSnapshot): DomainType | null {
    if (route.data?.['domain']) {
      return route.data['domain'] as DomainType;
    }
    for (const child of route.children) {
      const found = this.findDomainInRouteTree(child);
      if (found) return found;
    }
    return null;
  }

  /**
   * Restituisce il prefisso di dominio per la costruzione degli URL
   * @returns 'supplier' per contesto SUPPLIERS, 'professional' per PROFESSIONALS
   */
  getDomainPrefix(): 'supplier' | 'professional' {
    return this.isSupplierContext() ? 'supplier' : 'professional';
  }

  /**
   * Imposta manualmente il contesto SUPPLIERS
   */
  setSupplierContext(): void {
    const newContext: NavigationContext = {
      domainType: 'SUPPLIERS'
    };
    
    if (JSON.stringify(this.context$.value) !== JSON.stringify(newContext)) {
      this.context$.next(newContext);
      console.log('[NavigationContext] Set to SUPPLIERS');
    }
  }

  /**
   * Imposta manualmente il contesto PROFESSIONALS
   */
  setProfessionalContext(): void {
    const newContext: NavigationContext = {
      domainType: 'PROFESSIONALS'
    };
    
    if (JSON.stringify(this.context$.value) !== JSON.stringify(newContext)) {
      this.context$.next(newContext);
      console.log('[NavigationContext] Set to PROFESSIONALS');
    }
  }

  /**
   * Ottiene il contesto corrente (snapshot)
   */
  getCurrentContext(): NavigationContext {
    return this.context$.value;
  }

  /**
   * Ottiene il domainType corrente
   */
  getCurrentDomainType(): DomainType {
    return this.context$.value.domainType;
  }

  /**
   * Observable del contesto per reagire ai cambiamenti
   */
  getContext$(): Observable<NavigationContext> {
    return this.context$.asObservable();
  }

  /**
   * Verifica se siamo nel contesto SUPPLIERS
   */
  isSupplierContext(): boolean {
    return this.context$.value.domainType === 'SUPPLIERS';
  }

  /**
   * Verifica se siamo nel contesto PROFESSIONALS
   */
  isProfessionalContext(): boolean {
    return this.context$.value.domainType === 'PROFESSIONALS';
  }
}
